import {
  ConnectionQuality,
  InternalRoomOptions,
  LocalTrack,
  ParticipantEvent,
  RemoteParticipant,
  Room,
  RoomEvent,
  Track,
  VideoPresets,
  VideoPreset,
} from 'livekit-client';
import { action, makeObservable, observable } from 'mobx';

import {
  ICallProviderFacade,
  ICallProviderJoinParams,
  IProviderClientHandlers,
  IProviderClientParams,
  ProviderUid,
} from '@gem/call/CallProviders/callProviders.types';
import { CallVideoResolution, IVideoQualitySettings } from '@gem/call/Model/callModel.types';
import { CallBlurType } from '@gem/call/constants';
import { IDevices } from '@gem/call/tasks/getDefaultDevices';
import { logger } from '@gem/modules/logger';
import dateService from '@gem/services/date-service';
import { platformInfo } from '@gem/utils';

import { CallConnectionQuality } from '../callProviders.constants';
import { LiveKitProviderOpponent } from './LiveKitProviderOpponent';
import { LiveKitProviderPublisher } from './LiveKitProviderPublisher';
import { LiveKitBlurProcessor } from './LivekitProviderProcessor/LivekitProviderProcessor';
import { LiveKitShareScreenProvider } from './LivekitShareScreenProvider';

export const BlurOptions = {
  [CallBlurType.Small]: 25,
  [CallBlurType.Large]: 65,
  [CallBlurType.None]: 0,
};

export class LiveKitProvider implements ICallProviderFacade {
  canUseBlur = !platformInfo.isSafari && !platformInfo.isMobile;
  needToGetScreenSharingToken = false;
  private providerUrl: string;
  private isDisposing = false;
  private shareScreenClient: LiveKitShareScreenProvider | null = null;
  private subscribers = new Map<ProviderUid, LiveKitProviderOpponent>();
  private localVideoTrack: LocalTrack | null = null;
  private localAudioTrack: LocalTrack | null = null;
  private backgroundProcessor: LiveKitBlurProcessor | null = null;
  private channelId: string;
  private eventHandlers: Partial<IProviderClientHandlers> = {};
  private isPublished = false;
  private isAudioProcessing = false;
  private isVideoProcessing = false;
  private publishPromises: Promise<any>[] = [];

  hasAudio = false;
  hasVideo = false;
  isLocalAudioResetting = false;
  private blurType = CallBlurType.None;
  @observable publisher = new LiveKitProviderPublisher(this);
  @observable publisherVolumeLevel = 0;
  devices: IDevices;
  isJoined = false;
  room: Room | null = null;

  constructor(params: { blurType?: CallBlurType; providerUrl: string }) {
    makeObservable(this);
    this.providerUrl = params.providerUrl;
    if (params.blurType && this.canUseBlur) {
      this.blurType = params.blurType;
    }

    if (this.canUseBlur) {
      this.backgroundProcessor = new LiveKitBlurProcessor();
    }
  }

  getClientSettings(
    videoQualitySettings: IVideoQualitySettings | null
  ): Partial<InternalRoomOptions> {
    const settings: Partial<InternalRoomOptions> = {
      adaptiveStream: true,
    };

    if (videoQualitySettings) {
      const videoPresetMap: Record<CallVideoResolution, VideoPreset> = {
        [CallVideoResolution.P360]: VideoPresets.h360,
        [CallVideoResolution.P480]: new VideoPreset(854, 480, 800_000, 30),
        [CallVideoResolution.P720]: VideoPresets.h720,
        [CallVideoResolution.P1080]: VideoPresets.h1080,
      };

      const medium = videoPresetMap[videoQualitySettings.medium.resolution];
      medium.encoding.maxFramerate = videoQualitySettings.medium.maxFramerate;
      medium.encoding.maxBitrate = videoQualitySettings.medium.maxBitrate;

      const low = videoPresetMap[videoQualitySettings.low.resolution];
      medium.encoding.maxFramerate = videoQualitySettings.low.maxFramerate;
      medium.encoding.maxBitrate = videoQualitySettings.low.maxBitrate;

      settings.publishDefaults = {
        videoEncoding: {
          maxBitrate: videoQualitySettings.high.maxBitrate,
          maxFramerate: videoQualitySettings.high.maxFramerate,
        },
        videoSimulcastLayers: [low, medium],
      };

      settings.videoCaptureDefaults = {
        resolution: {
          width: videoPresetMap[videoQualitySettings.high.resolution].width,
          height: videoPresetMap[videoQualitySettings.high.resolution].height,
          frameRate: videoQualitySettings.high.maxFramerate,
        },
      };
    }

    return settings;
  }

  // initialize all handlers and session
  async createClient(params: IProviderClientParams): Promise<void> {
    this.room = new Room(this.getClientSettings(params.videoQualitySettings));
    this.devices = params.devices;

    if (params.handlers) {
      this.eventHandlers = params.handlers;
    }
    this.room.on(RoomEvent.Connected, this.onConnectionCreated);
    this.room.localParticipant.on(
      RoomEvent.ConnectionQualityChanged,
      this.onConnectionQualityChanged.bind(this)
    );
    this.room.on(RoomEvent.ParticipantConnected, this.handleUserJoined.bind(this));
    this.room.on(RoomEvent.ParticipantDisconnected, this.handleUserLeft.bind(this));

    this.room.on(RoomEvent.SignalReconnecting, (): void => {
      this.onConnectionQualityChanged(ConnectionQuality.Lost);
    });

    this.room.on(RoomEvent.Reconnected, (): void => {
      this.onConnectionQualityChanged(ConnectionQuality.Excellent);
    });

    this.room.localParticipant.on(
      ParticipantEvent.IsSpeakingChanged,
      this.onPublisherSpeakingChanged.bind(this)
    );
  }

  private onConnectionQualityChanged = (quality: ConnectionQuality): void => {
    const liveKitConnectionQualityMap: Record<ConnectionQuality, CallConnectionQuality> = {
      [ConnectionQuality.Unknown]: CallConnectionQuality.Unknown,
      [ConnectionQuality.Lost]: CallConnectionQuality.Lost,
      [ConnectionQuality.Good]: CallConnectionQuality.Good,
      [ConnectionQuality.Excellent]: CallConnectionQuality.Excellent,
      [ConnectionQuality.Poor]: CallConnectionQuality.Poor,
    };
    if (this.eventHandlers.onConnectionQualityChanged) {
      this.eventHandlers.onConnectionQualityChanged(liveKitConnectionQualityMap[quality]);
    }
  };

  // check our connection and send Join event to our SSE
  private onConnectionCreated = (): void => {
    if (this.channelId && this.eventHandlers.onJoin) {
      this.eventHandlers.onJoin({ channelId: this.channelId });
    }
    this.isJoined = true;
  };

  // subscribe to remote user
  private handleUserJoined = async (participant: RemoteParticipant): Promise<void> => {
    const userId = participant.identity;

    // after subscribing to user we create our facade class for system with subscriber
    const subscriber = new LiveKitProviderOpponent({
      subscriber: participant,
      subscriberId: userId,
      onUserMediaChanged: this.eventHandlers.onUserMediaChanged,
      onUserVolumeLevelChanged: this.eventHandlers.onUserVolumeLevelChanged,
      onUserHandAction: this.eventHandlers.onUserHandAction,
      parent: this,
    });

    const attributes = participant.attributes;
    const hasHandRaised = ['true', 'false'].includes(attributes.isHandRaised);
    const hasHandRaisedAt = dateService.isValidTimestamp(attributes.handRaisedAt);

    // set it to our private map
    this.subscribers.set(userId, subscriber);

    if (this.eventHandlers.onUserJoined) {
      // and send to system
      this.eventHandlers.onUserJoined({
        hasAudio: subscriber.hasAudio,
        hasVideo: subscriber.hasVideo,
        isHandRaised: hasHandRaised ? attributes.isHandRaised === 'true' : false,
        handRaisedAt: hasHandRaisedAt ? +attributes.handRaisedAt : null,
        uid: subscriber.uid,
      });
    }
  };

  private onPublisherSpeakingChanged(isSpeaking: boolean): void {
    if (this.eventHandlers.onPublisherSpeakingChanged) {
      this.eventHandlers.onPublisherSpeakingChanged(isSpeaking);
    }
  }

  // if user left we remove it from our private store
  private handleUserLeft = (participant: RemoteParticipant): void => {
    const connectionId = participant.identity;
    if (this.eventHandlers.onUserLeft) {
      this.eventHandlers.onUserLeft(connectionId);
    }
    this.subscribers.delete(connectionId);
  };

  async leave(): Promise<void> {
    if (this.room) {
      await this.room.disconnect(true);
    }
    if (this.eventHandlers.onLeave) {
      this.eventHandlers.onLeave({ channelId: this.channelId });
    }
  }

  async join(params: ICallProviderJoinParams): Promise<void> {
    const { token, channelId } = params;

    try {
      this.channelId = channelId;

      if (!this.room) {
        return;
      }
      await this.room.connect(this.providerUrl, token);
      // after connect we are getting current opponents from call
      this.room.remoteParticipants.forEach(this.handleUserJoined.bind(this));
    } catch (e) {
      logger.get('CallProviders').error('LiveKit.join', e);
    }
  }

  // method for streaming
  async setSomeUserTypeStream(_uid: ProviderUid, _type: number): Promise<void> {
    // ToDO: [calls] realize!!!!;
  }

  // publish our tracks to the stream
  @action
  async publish(): Promise<void> {
    if (!this.room) {
      return;
    }

    if (
      this.localVideoTrack &&
      !this.room.localParticipant.getTrackPublication(Track.Source.Camera)
    ) {
      await this.room.localParticipant.publishTrack(this.localVideoTrack);
    }

    if (
      this.localAudioTrack &&
      !this.room.localParticipant.getTrackPublication(Track.Source.Microphone)
    ) {
      await this.room.localParticipant.publishTrack(this.localAudioTrack);
    }

    this.isPublished = true;
  }

  @action
  async muteAudio(): Promise<boolean> {
    if (this.isAudioProcessing || !this.room) {
      return false;
    }

    this.isAudioProcessing = true;

    try {
      // if it's already published we use sdk method
      if (
        this.localAudioTrack &&
        this.room.localParticipant.getTrackPublication(Track.Source.Microphone)
      ) {
        await this.room.localParticipant.setMicrophoneEnabled(false);
        // else we are stopping track and remove it
      } else if (this.localAudioTrack) {
        await this.room.localParticipant.setMicrophoneEnabled(false);
        this.localAudioTrack.off('ended', this.onTrackEnded);
        this.localAudioTrack.stop();
        this.localAudioTrack = null;
      }

      this.isAudioProcessing = false;
      this.hasAudio = false;

      return true;
    } catch {
      this.isAudioProcessing = false;

      return false;
    }
  }

  private onTrackEnded = async (): Promise<void> => {
    if (this.eventHandlers?.onLocalAudioTrackEnd) {
      this.eventHandlers.onLocalAudioTrackEnd(this.devices.audioinput || undefined);
    }
  };

  // if track ended we restart it and publish
  private resetLocalAudioTrack = async (): Promise<void> => {
    if (!this.localAudioTrack || this.isLocalAudioResetting || !this.room) {
      return;
    }
    try {
      this.isLocalAudioResetting = true;

      this.localAudioTrack.off('ended', this.onTrackEnded);
      const trackPublication = this.room.localParticipant.getTrackPublication(
        Track.Source.Microphone
      );
      const track = trackPublication?.track;
      if (track) {
        track.stop();
      }
      const localAudioTrack = await this.room.localParticipant.createTracks({
        audio: this.devices.audioinput ? { deviceId: { exact: this.devices.audioinput } } : true,
      });
      this.localAudioTrack.on('ended', this.onTrackEnded);
      this.localAudioTrack = localAudioTrack[0];
      if (track) {
        await track.replaceTrack(localAudioTrack[0].mediaStreamTrack);
      }

      if (this.eventHandlers.onLocalAudioTrackStart) {
        this.eventHandlers.onLocalAudioTrackStart(this.devices.audioinput || undefined);
      }

      await this.room.localParticipant.setMicrophoneEnabled(this.hasAudio);

      if (this.isDisposing && this.localAudioTrack) {
        this.localAudioTrack.stop();
      }
      this.isLocalAudioResetting = false;
    } catch (e) {
      if (this.localAudioTrack) {
        this.localAudioTrack.stop();
        this.localAudioTrack = null;
        this.isLocalAudioResetting = false;
      }
    }
  };

  private resetLocalVideoTrack = async (needToTurnOn?: boolean): Promise<void> => {
    if (!this.localVideoTrack || !this.room) {
      return;
    }
    try {
      if (this.blurType !== CallBlurType.None && this.backgroundProcessor) {
        this.backgroundProcessor.destroy();
      }
      await this.room.localParticipant.unpublishTrack(this.localVideoTrack);
      this.localVideoTrack.stop();
      this.localVideoTrack = null;

      const localVideoTrack = await this.room.localParticipant.createTracks({
        video: this.devices.videoinput ? { deviceId: this.devices.videoinput } : true,
      });

      this.localVideoTrack = localVideoTrack[0];

      if (this.blurType !== CallBlurType.None) {
        await this.setBackground(this.localVideoTrack, this.blurType);
      }

      if (this.isPublished && !needToTurnOn) {
        await this.room.localParticipant.publishTrack(this.localVideoTrack);
        await this.room.localParticipant.setCameraEnabled(this.hasVideo);
      }
      if (this.isPublished && needToTurnOn) {
        await this.room.localParticipant.publishTrack(this.localVideoTrack);
        await this.room.localParticipant.setCameraEnabled(true);
        this.hasVideo = true;
      }
      if (this.isDisposing && this.localVideoTrack) {
        this.localVideoTrack.stop();
      }
    } catch (e) {
      if (this.localVideoTrack) {
        this.localVideoTrack.stop();
        this.localVideoTrack = null;
      }
    }
  };

  // unmute audio
  @action
  async unmuteAudio(needToPublish?: boolean): Promise<boolean> {
    if (this.isAudioProcessing || !this.room) {
      return false;
    }

    this.isAudioProcessing = true;

    try {
      const promise = (async (): Promise<void> => {
        if (this.isDisposing || !this.room) {
          return;
        }
        // if we've already created track and published, we just turn on the mic.
        if (
          this.localAudioTrack &&
          this.room.localParticipant.getTrackPublication(Track.Source.Microphone)
        ) {
          const track = this.room.localParticipant.getTrackPublication(Track.Source.Microphone);
          const isTrackEnded = track?.audioTrack?.mediaStreamTrack.readyState === 'ended';

          if (isTrackEnded) {
            await this.resetLocalAudioTrack();
          }
          await this.room.localParticipant.setMicrophoneEnabled(true, {
            deviceId: this.devices.audioinput ? { exact: this.devices.audioinput } : undefined,
          });
        } else if (!this.localAudioTrack) {
          // else we are creating track
          const localAudioTrack = await this.room.localParticipant.createTracks({
            audio: this.devices.audioinput
              ? { deviceId: { exact: this.devices.audioinput } }
              : true,
          });

          this.localAudioTrack = localAudioTrack[0];
          // set track ended handler for resetting track.
          this.localAudioTrack.on('ended', this.onTrackEnded);
          if (this.eventHandlers.onLocalAudioTrackStart) {
            this.eventHandlers.onLocalAudioTrackStart(this.devices.audioinput || undefined);
          }

          // and publish it if needed
          if (needToPublish && !this.isDisposing) {
            await this.room.localParticipant.publishTrack(this.localAudioTrack);
            this.isPublished = true;
          }
          if (this.isDisposing && this.localAudioTrack) {
            this.localAudioTrack.stop();
            this.localAudioTrack = null;
          }
        }
      })();

      this.publishPromises.push(promise);
      await promise;

      this.hasAudio = true;
      this.isAudioProcessing = false;

      return true;
    } catch (e) {
      this.isAudioProcessing = false;

      return false;
    }
  }

  // unpublish our media from the stream
  @action
  async unpublish(): Promise<void> {
    if (!this.room) {
      return;
    }
    if (
      this.localVideoTrack &&
      this.room.localParticipant.getTrackPublication(Track.Source.Camera)
    ) {
      await this.room.localParticipant.unpublishTrack(this.localVideoTrack);
      this.localVideoTrack.stop();
      this.localVideoTrack = null;
    }

    if (
      this.localAudioTrack &&
      this.room.localParticipant.getTrackPublication(Track.Source.Microphone)
    ) {
      await this.room.localParticipant.unpublishTrack(this.localAudioTrack);
      this.localAudioTrack.stop();
      this.localAudioTrack.off('ended', this.onTrackEnded);
      this.localAudioTrack = null;
    }

    this.isPublished = false;
  }

  @action
  async unmuteVideo(needToPublish?: boolean): Promise<boolean> {
    if (this.isVideoProcessing) {
      return false;
    }

    this.isVideoProcessing = true;

    try {
      const promise = (async (): Promise<void> => {
        if (!this.room) {
          return;
        }
        // if we've already created track and published, we just turn on the camera.
        if (this.room.localParticipant.getTrackPublication(Track.Source.Camera)) {
          if (this.blurType !== CallBlurType.None && this.backgroundProcessor) {
            this.backgroundProcessor.restart();
          }
          await this.room.localParticipant.setCameraEnabled(true);
        } else if (!this.localVideoTrack) {
          // else we are creating track
          const localVideoTracks = await this.room.localParticipant.createTracks({
            video: this.devices.videoinput ? { deviceId: this.devices.videoinput } : true,
          });

          this.localVideoTrack = localVideoTracks[0];

          if (this.blurType !== CallBlurType.None) {
            await this.setBackground(this.localVideoTrack, this.blurType);
          }
          // and publish it to stream if it's needed
          if (needToPublish) {
            await this.room.localParticipant.publishTrack(this.localVideoTrack);
            this.isPublished = true;
          }
        }
        if (this.isDisposing && this.localVideoTrack) {
          this.localVideoTrack.stop();
        }
      })();

      this.publishPromises.push(promise);
      await promise;
      this.hasVideo = true;
      this.isVideoProcessing = false;

      return true;
    } catch (e) {
      if (this.localVideoTrack) {
        this.localVideoTrack.stop();
        this.localVideoTrack = null;
      }
      this.isVideoProcessing = false;

      return false;
    }
  }

  @action
  async muteVideo(): Promise<boolean> {
    if (this.isVideoProcessing || !this.room) {
      return false;
    }

    this.isVideoProcessing = true;
    try {
      if (this.blurType !== CallBlurType.None && this.backgroundProcessor) {
        this.backgroundProcessor.destroy();
      }
      if (
        this.localVideoTrack &&
        this.room.localParticipant.getTrackPublication(Track.Source.Camera)
      ) {
        await this.room.localParticipant.setCameraEnabled(false);
        // else we just stop the track and delete it
      } else if (this.localVideoTrack) {
        this.localVideoTrack.stop();
        this.localVideoTrack = null;
      }

      this.hasVideo = false;
      this.isVideoProcessing = false;

      return true;
    } catch {
      this.isVideoProcessing = false;

      if (this.localAudioTrack) {
        this.localAudioTrack.stop();
        this.localAudioTrack = null;
      }

      return false;
    }
  }

  // ToDO: [calls] rewrite part of publishing for streams
  async startScreenShare(params: {
    needToPublish?: boolean;
    electronSourceId?: string;
    token: string;
    onStopSharingScreen?: () => void;
    track?: MediaStreamTrack;
  }): Promise<boolean> {
    const shareScreenClient = new LiveKitShareScreenProvider(this.providerUrl);

    await shareScreenClient.join({
      token: params.token,
      channelId: this.channelId,
    });

    const onEnded = async (): Promise<void> => {
      await this.stopScreenShare();
      if (params.onStopSharingScreen) {
        params.onStopSharingScreen();
      }
    };

    this.shareScreenClient = shareScreenClient;

    return await shareScreenClient.startScreenSharing({
      electronSourceId: params.electronSourceId,
      needToPublish: params.needToPublish,
      onEnded,
      track: params.track,
    });
  }

  async setHandRaised(value: boolean, handRaisedAt: number | null): Promise<boolean> {
    try {
      if (!this.room) {
        return false;
      }
      await this.room.localParticipant.setAttributes({
        isHandRaised: String(value),
        handRaisedAt: String(handRaisedAt),
      });
      return true;
    } catch {
      return false;
    }
  }

  playOpponentVideo(container: HTMLDivElement, uid: ProviderUid): void {
    const subscriber = this.subscribers.get(uid);
    subscriber?.playVideo(container);
  }

  stopOpponentVideo(uid: ProviderUid): void {
    const subscriber = this.subscribers.get(uid);
    subscriber?.stopVideo();
  }

  playPublisherVideo(container: HTMLDivElement): HTMLVideoElement | null {
    return this.publisher.playVideo(container);
  }

  stopPublisherVideo(container?: HTMLVideoElement): void {
    this.publisher.stopVideo(container);
  }

  async changeMicrophone(device: string | null): Promise<void> {
    this.devices.audioinput = device;
    await this.resetLocalAudioTrack();
  }

  async changeCamera(device: string | null, needToTurnOn?: boolean): Promise<void> {
    this.devices.videoinput = device;
    await this.resetLocalVideoTrack(needToTurnOn);
  }

  @action
  async stopScreenShare(): Promise<void> {
    if (this.shareScreenClient) {
      await this.shareScreenClient.stopScreenShare();
      this.shareScreenClient = null;
    }
  }

  async setBackground(track: LocalTrack<Track.Kind>, blurType: CallBlurType): Promise<void> {
    if (!this.backgroundProcessor) {
      return;
    }

    this.backgroundProcessor.setTrackConstraints(
      track.constraints.width as number,
      track.constraints.height as number
    );
    this.backgroundProcessor.setBlurValue(BlurOptions[blurType]);
    await track.setProcessor(this.backgroundProcessor);
  }

  async setBlur(blurType: CallBlurType): Promise<void> {
    if (!this.backgroundProcessor) {
      return;
    }
    this.blurType = blurType;

    if (
      !this.localVideoTrack ||
      (blurType === CallBlurType.None && !this.backgroundProcessor.isStarted)
    ) {
      return;
    }

    if (blurType === CallBlurType.None) {
      this.localVideoTrack?.stopProcessor();
      return;
    }

    if (!this.backgroundProcessor.isStarted) {
      this.setBackground(this.localVideoTrack, this.blurType);
    } else {
      this.backgroundProcessor.setBlurValue(BlurOptions[this.blurType]);
    }
  }

  playOpponentsAudio(): void {
    this.subscribers.forEach((subscriber: LiveKitProviderOpponent): void => {
      subscriber.startAudioTrack();
    });
  }

  changeSpeaker(deviceId: string): void {
    this.devices.audiooutput = deviceId;
    this.subscribers.forEach((subscriber) => {
      subscriber.changeSpeaker();
    });
  }

  async dispose(): Promise<boolean> {
    this.isDisposing = true;
    if (!this.room) {
      return false;
    }
    if (this.backgroundProcessor) {
      await this.backgroundProcessor.dispose();
      this.backgroundProcessor = null;
    }
    await Promise.all(this.publishPromises);
    if (this.localAudioTrack) {
      this.localAudioTrack.stop();
      this.localAudioTrack = null;
    }
    if (this.localVideoTrack) {
      this.localVideoTrack.stop();
      this.localVideoTrack = null;
    }
    this.room.off(RoomEvent.Connected, this.onConnectionCreated);
    this.room.off(RoomEvent.ParticipantConnected, this.handleUserJoined);
    this.room.off(RoomEvent.ParticipantDisconnected, this.handleUserLeft);
    if (this.shareScreenClient) {
      await this.shareScreenClient.stopScreenShare();
      this.shareScreenClient = null;
    }
    await this.leave();

    return true;
  }
}
